import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, HelpCircle, FileBarChart, TrendingUp, Upload, Calculator, Target, AlertCircle, Mail, BarChart3, Shield, Wallet, Bell, CalendarDays } from 'lucide-react';
import tradeCompassLogo from '@/assets/tradecompass-logo.png';

const Help = () => {
  const navigate = useNavigate();

  const faqCategories = [
    {
      title: "Getting Started",
      icon: TrendingUp,
      color: "bg-primary/10 text-primary",
      faqs: [
        {
          question: "What is GainTracker?",
          answer: "GainTracker is a professional portfolio analysis platform that helps you track, analyze, and optimize your investment positions. It supports Interactive Brokers (IBKR) imports and provides detailed insights into your portfolio performance, including profit targets, stop-loss recommendations, and capital recovery strategies. It also features an AI-powered Content Summary tool for YouTube video analysis."
        },
        {
          question: "How do I start using GainTracker?",
          answer: "Simply sign up with your email address and start importing your portfolio data. You can import positions via Excel files or IBKR Activity Statements. Once imported, GainTracker will automatically analyze your positions and provide actionable recommendations."
        },
        {
          question: "Is my data secure?",
          answer: "Yes, absolutely. All your data is stored securely with row-level security (RLS), meaning only you can access your own positions and orders. We use industry-standard encryption and authentication to protect your information."
        }
      ]
    },
    {
      title: "Importing Data",
      icon: Upload,
      color: "bg-accent/10 text-accent",
      faqs: [
        {
          question: "How do I import from IBKR?",
          answer: "Navigate to 'IBKR Full Analysis PF' from the dashboard. You can upload your IBKR Activity Statement CSV file which contains all your trades, dividends, cash transactions, and positions. The system will parse the file and display comprehensive analysis including realized P/L, dividend income, and portfolio overview."
        },
        {
          question: "What file formats are supported?",
          answer: "GainTracker supports Excel files (.xlsx, .xls) for general portfolio imports, and CSV files for IBKR Activity Statements. Make sure your Excel file includes columns for ticker, quantity, buy price, and optionally currency and ISIN."
        },
        {
          question: "How does the Content Summary tool work?",
          answer: "Navigate to 'Content Summary' from the dashboard and paste any YouTube video URL. The tool will extract the video transcript and use AI to generate a comprehensive, color-coded summary with key points, detailed analysis, takeaways, and topic tags — all streamed in real-time."
        },
        {
          question: "Can I manually add positions?",
          answer: "Yes! Click the 'Add Position' button on the main dashboard to manually enter individual stock positions. You'll need to provide the ticker symbol, quantity, and buy price at minimum."
        }
      ]
    },
    {
      title: "Understanding Analysis",
      icon: Calculator,
      color: "bg-success/10 text-success",
      faqs: [
        {
          question: "What do the alert colors mean?",
          answer: "GainTracker uses a tiered alert system based on your position gains:\n\n• Yellow (15%+ gain): Suggests recovering 20% of your initial investment\n• Orange (30%+ gain): Suggests recovering 40% of your initial investment\n• Red (45%+ gain): Suggests recovering 100% of your initial investment (selling 50% of shares)\n\nThese alerts help you systematically take profits and reduce risk in your portfolio."
        },
        {
          question: "How is 'Added Value' calculated?",
          answer: "Added Value represents the unrealized profit or loss in CHF (Swiss Francs) for each position. It's calculated by taking the difference between the current market value and your cost basis, converted to CHF using current exchange rates."
        },
        {
          question: "What is the Stock Scorecard?",
          answer: "The Stock Scorecard provides a quick overview of positions that have actionable recommendations. It shows positions at different gain thresholds (15%, 30%, 45%) and suggests the number of shares to sell to recover your initial investment gradually."
        },
        {
          question: "How does the Fibonacci Retracement work?",
          answer: "The Fibonacci Retracement calculator helps you identify potential support and resistance levels for any stock. Enter a high and low price, and the tool calculates the key Fibonacci levels (23.6%, 38.2%, 50%, 61.8%, 78.6%) that traders commonly use for technical analysis."
        }
      ]
    },
    {
      title: "IBKR Full Analysis",
      icon: FileBarChart,
      color: "bg-orange-500/10 text-orange-600",
      faqs: [
        {
          question: "What's included in the IBKR analysis?",
          answer: "The IBKR Full Analysis provides comprehensive parsing of your Activity Statement including:\n\n• Portfolio Overview with all open positions\n• Complete trade history with realized P/L\n• Dividend and withholding tax breakdown\n• Cash transactions (deposits, withdrawals, fees)\n• Financial summary with NAV and performance metrics\n• Date range filtering for all transaction types"
        },
        {
          question: "How do I export dividend data for taxes?",
          answer: "In the Dividends tab of IBKR Analysis, click the 'Export for Tax' button to download a CSV file containing all dividend information organized by ticker, with gross dividends, withholding taxes, and net amounts - perfect for tax reporting."
        },
        {
          question: "What is the Total Fees Summary?",
          answer: "The Total Fees Summary card in the Cash tab aggregates all fees across your account including trading commissions, account fees, and withholding taxes, giving you a complete picture of your total costs."
        }
      ]
    },
    {
      title: "SMA Surveillance & Ichimoku",
      icon: BarChart3,
      color: "bg-purple-500/10 text-purple-600",
      faqs: [
        {
          question: "What is SMA Surveillance?",
          answer: "SMA Surveillance monitors your positions against key Simple Moving Averages (20-day and 50-day). It helps identify when stocks are trading above or below these important technical levels, which can signal potential buy or sell opportunities."
        },
        {
          question: "What is Ichimoku analysis?",
          answer: "Ichimoku Cloud is a comprehensive technical analysis indicator that provides information about support/resistance levels, trend direction, momentum, and potential buy/sell signals. Our analysis includes the Tenkan-sen, Kijun-sen, Senkou Span A/B (the cloud), and trading signals."
        },
        {
          question: "How do I interpret the price line in charts?",
          answer: "The price line is shown in bold yellow for maximum visibility. When the price is above the Ichimoku cloud, it suggests a bullish trend; when below, it suggests bearish conditions. The cloud itself acts as dynamic support/resistance."
        }
      ]
    },
    {
      title: "DB Orders",
      icon: Target,
      color: "bg-blue-500/10 text-blue-600",
      faqs: [
        {
          question: "What is DB Order?",
          answer: "DB Order is a feature for creating and tracking trading orders. You can create buy or sell orders with specific parameters like limit prices, validity dates, and stock exchange codes. Each order generates a PDF document and is tracked in your order history."
        },
        {
          question: "How do order PDFs work?",
          answer: "When you submit an order, a PDF document is automatically generated containing all order details. This PDF is stored securely and can be downloaded at any time from the archived orders table."
        },
        {
          question: "What order types are supported?",
          answer: "GainTracker supports Normal (market) orders and Limit orders. For limit orders, you must specify the limit price at which you want the order to execute."
        }
      ]
    },
    {
      title: "Dividend Dashboard",
      icon: Wallet,
      color: "bg-amber-500/10 text-amber-600",
      faqs: [
        {
          question: "What does the Dividend Dashboard show?",
          answer: "The Dividend Dashboard tracks all your dividend income from IBKR imports or file imports. It displays total dividends received, yield on cost, monthly income trends via bar charts, top dividend payers, and a projected annual income estimate."
        },
        {
          question: "Where does the dividend data come from?",
          answer: "Dividend data can come from two sources:\n\n• IBKR Activity Statements: Automatically parsed from your imported statement\n• File Import: Upload an Excel file (PRVFIN tab) or IBKR Activity Statement directly from the Dividend Dashboard using the Import button"
        },
        {
          question: "How do I import a file for dividend analysis?",
          answer: "Click the 'Import' button in the top-right header of the Dividend Dashboard. This opens a collapsible panel where you can upload an Excel file (.xlsx, .xls) with the PRVFIN sheet format, or an IBKR Activity Statement. The imported positions will be used for dividend calculations."
        },
        {
          question: "How do I save and load dividend analyses?",
          answer: "After running an analysis, click the 'Save' button in the header to store the current results as a snapshot. Saved analyses appear in the 'Saved Analyses' bar below the import panel, showing date, position count, and source. Click any snapshot to restore it, or use the trash icon to delete it. Snapshots are also accessible from the global 'Saved Data' dropdown in the top navigation."
        },
        {
          question: "How is projected annual income calculated?",
          answer: "Projected annual income is estimated by extrapolating your existing dividend payments over a 12-month period based on historical data from your IBKR imports."
        }
      ]
    },
    {
      title: "Performance Tracker",
      icon: BarChart3,
      color: "bg-blue-500/10 text-blue-600",
      faqs: [
        {
          question: "What metrics does the Performance Tracker provide?",
          answer: "The Performance Tracker shows your overall portfolio return percentage, identifies your best and worst performing positions, compares invested vs. current value, and displays added value in CHF across all positions with horizontal bar charts."
        },
        {
          question: "How are returns calculated?",
          answer: "Returns are calculated based on the difference between your current position values (using live prices) and your original cost basis (buy price × quantity), expressed as a percentage."
        },
        {
          question: "How do I import data for performance analysis?",
          answer: "Click 'Import' in the Performance Tracker header to upload an Excel file (PRVFIN tab) or IBKR Activity Statement. The imported positions will be analyzed for performance metrics, gains/losses, and comparative charts."
        },
        {
          question: "Can I save and reload performance analyses?",
          answer: "Yes. After analysis, click 'Save' to store a snapshot. All saved analyses appear in the 'Saved Analyses' history bar with timestamp and position count. Click any entry to restore it instantly. Snapshots are also available in the global 'Saved Data' menu."
        }
      ]
    },
    {
      title: "Price Alerts & Watchlist",
      icon: Bell,
      color: "bg-rose-500/10 text-rose-600",
      faqs: [
        {
          question: "How do I set a price alert?",
          answer: "Navigate to Price Alerts & Watchlist, enter a ticker symbol, choose an alert type (Price Above, Price Below, or % Change), set your target price or percentage threshold, and optionally add notes. Click 'Add' to save it to your cloud-persisted watchlist."
        },
        {
          question: "What alert types are available?",
          answer: "Three alert types are supported:\n\n• Price Above: triggers when the stock price exceeds your target\n• Price Below: triggers when the stock price drops below your target\n• % Change: triggers when the stock moves by the specified percentage"
        },
        {
          question: "Can I track stocks I don't own?",
          answer: "Yes! The watchlist is independent of your portfolio positions. You can add any ticker symbol to monitor, making it ideal for tracking potential buy candidates."
        }
      ]
    },
    {
      title: "Risk Dashboard",
      icon: AlertCircle,
      color: "bg-red-600/10 text-red-600",
      faqs: [
        {
          question: "What is the Risk Score?",
          answer: "The Risk Score is a composite metric from 0 to 100 that evaluates your portfolio's overall risk level based on concentration, number of positions, and individual position weights. A lower score indicates better diversification."
        },
        {
          question: "What is Value at Risk (VaR)?",
          answer: "VaR 95% estimates the maximum expected loss over a given period with 95% confidence. For example, a VaR of CHF 5,000 means there's only a 5% chance your portfolio could lose more than CHF 5,000 in the specified timeframe."
        },
        {
          question: "How does concentration analysis work?",
          answer: "The concentration analysis shows the weight of each position relative to your total portfolio value. It highlights positions that exceed recommended allocation thresholds, helping you identify over-concentration risks."
        },
        {
          question: "How do I import data for risk analysis?",
          answer: "Click 'Import' in the Risk Dashboard header to upload an Excel file (PRVFIN tab) or IBKR Activity Statement. The system will calculate risk metrics including VaR, concentration, and risk score based on the imported positions."
        },
        {
          question: "Can I save and compare risk analyses over time?",
          answer: "Yes. Click 'Save' after running an analysis to store a snapshot. Saved risk analyses appear in the history bar and can be restored at any time. This lets you compare how your risk profile changes over time. All snapshots are also accessible from the global 'Saved Data' dropdown."
        }
      ]
    },
    {
      title: "Earnings Calendar",
      icon: CalendarDays,
      color: "bg-orange-500/10 text-orange-600",
      faqs: [
        {
          question: "What is the Earnings Calendar?",
          answer: "The Earnings Calendar shows upcoming and recent earnings report dates for all stocks in your portfolio. It displays analyst EPS and revenue estimates from Financial Modeling Prep (FMP), and for past reports, shows actual results with beat/miss indicators."
        },
        {
          question: "Where does the earnings data come from?",
          answer: "The module connects to the Financial Modeling Prep (FMP) API to fetch live earnings calendar data and analyst estimates. It cross-references FMP's earnings schedule with your portfolio tickers to show only relevant events."
        },
        {
          question: "How do I import positions for earnings tracking?",
          answer: "Click 'Import' in the header to upload an Excel file (PRVFIN tab) or IBKR Activity Statement. The system will fetch earnings data from FMP for all imported tickers, showing upcoming dates, EPS estimates, and analyst coverage."
        },
        {
          question: "Can I save and reload earnings analyses?",
          answer: "Yes. Click 'Save' to store the current earnings snapshot. Saved analyses appear in the history bar and can be restored anytime. They're also accessible from the global 'Saved Data' dropdown menu in the top navigation."
        },
        {
          question: "What is the Email 3-Day Alert?",
          answer: "Click 'Email Alert' to receive an email notification listing all portfolio holdings with earnings reports within the next 3 days, including urgency badges (TODAY/TOMORROW), timing (BMO/AMC), and analyst estimates."
        },
        {
          question: "What do BMO and AMC mean?",
          answer: "BMO = Before Market Open (pre-market earnings release). AMC = After Market Close (after-hours earnings release). This timing helps you plan around potential price volatility."
        },
        {
          question: "How is the earnings surprise calculated?",
          answer: "Surprise % = ((EPS Actual - EPS Estimate) / EPS Estimate) × 100. A positive surprise means the company beat analyst expectations, while a negative surprise indicates a miss."
        }
      ]
    },
    {
      title: "Account & Security",
      icon: Shield,
      color: "bg-red-500/10 text-red-600",
      faqs: [
        {
          question: "How do I reset my data?",
          answer: "On the main dashboard, click the 'Reset Data' button to permanently delete all your imported positions. This action cannot be undone, so please be certain before confirming."
        },
        {
          question: "Can other users see my data?",
          answer: "No. All data in GainTracker is isolated by user through Row Level Security (RLS). Your positions, orders, and analysis are only visible to you when logged in with your account."
        },
        {
          question: "How do I sign out?",
          answer: "Click the 'Sign Out' button in the top-right corner of the dashboard to securely log out of your account."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-primary/10 rounded-2xl mb-6">
            <HelpCircle className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Help & FAQ
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about using GainTracker for your portfolio analysis
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <img 
              src={tradeCompassLogo} 
              alt="TradeCompass" 
              className="h-6 w-auto"
            />
            <span className="text-sm text-muted-foreground">
              Provided by TradeCompass
            </span>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: "Import Guide", icon: Upload, href: "#importing-data" },
            { label: "Analysis Help", icon: Calculator, href: "#understanding-analysis" },
            { label: "IBKR Guide", icon: FileBarChart, href: "#ibkr-full-analysis" },
            { label: "Contact", icon: Mail, href: "#contact" }
          ].map((link) => (
            <Card 
              key={link.label}
              className="cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all duration-300 group"
              onClick={() => {
                const element = document.querySelector(link.href);
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <CardContent className="flex flex-col items-center justify-center p-6">
                <link.icon className="h-8 w-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                <span className="font-medium text-foreground">{link.label}</span>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <Card 
              key={category.title} 
              id={category.title.toLowerCase().replace(/\s+/g, '-')}
              className="overflow-hidden shadow-lg border-0 bg-card/80 backdrop-blur-sm"
            >
              <CardHeader className="bg-gradient-to-r from-muted/50 to-muted/30 border-b">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${category.color}`}>
                    <category.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{category.title}</CardTitle>
                    <CardDescription className="mt-1">
                      {category.faqs.length} questions answered
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Accordion type="single" collapsible className="w-full">
                  {category.faqs.map((faq, faqIndex) => (
                    <AccordionItem 
                      key={faqIndex} 
                      value={`${categoryIndex}-${faqIndex}`}
                      className="border-b last:border-b-0"
                    >
                      <AccordionTrigger className="px-6 py-4 hover:bg-muted/30 text-left">
                        <span className="font-medium text-foreground pr-4">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6 pt-2">
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                          {faq.answer}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Section */}
        <Card id="contact" className="mt-12 overflow-hidden shadow-lg border-0 bg-gradient-to-br from-primary/5 to-accent/5">
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-xl mb-4">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Still have questions?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Can't find what you're looking for? Our support team is here to help you get the most out of GainTracker.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="gap-2">
                <Mail className="h-4 w-4" />
                Contact Support
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground">GainTracker</span>
          </div>
          <p>Professional Portfolio Analysis Platform</p>
        </div>
      </div>
    </div>
  );
};

export default Help;
